import React, { useMemo } from 'react';
import { useQCM } from '../../contexts/QCMContext';
import { useTranslation } from 'react-i18next';
import RoomForImprovement from './RoomForImprovement';
import QCMRating from './QCMRating';
import SectionContent from '../lab/SectionContent';
import { Doughnut } from 'react-chartjs-2';
import { Chart, ArcElement, Tooltip, Legend } from 'chart.js';
import ExamQCMSolution from './ExamQCMSolution';

Chart.register(ArcElement, Tooltip, Legend);

const QCMSolution = ({ stepData }) => {
    const { t } = useTranslation();
    const { currentQCM, lastFunnelResult } = useQCM();

    // Support pour les données d'étapes funnel QCM ou QCM normaux
    // Gérer trois formats: stepData.qcm, stepData direct, ou currentQCM.step.qcm (fallback)
    const qcmSource = useMemo(() => {
        if (stepData) {
            // Format avec stepData.qcm
            if (stepData.qcm) {
                return stepData.qcm;
            }
            // Format où les données QCM sont directement dans stepData
            if (stepData.correct_answers || stepData.correct_answer || stepData.distractors) {
                return stepData;
            }
        }
        // Fallback Funnel: currentQCM.step.qcm
        if (currentQCM?.step?.qcm) {
            return currentQCM.step.qcm;
        }
        // Format normal
        return currentQCM?.qcm;
    }, [stepData, currentQCM]);

    // Router: Use Exam style for regular QCMs, Funnel style for funnel QCMs
    if (!stepData && currentQCM?.questionType !== 'funnel-qcm') {
        return <ExamQCMSolution />;
    }

    if (!qcmSource) {
        return (
            <div className="text-center py-8">
                <p className="text-gray-400">{t('no_solution_available')}</p>
            </div>
        );
    }

    const q = qcmSource;
    const normalize = (opt) => {
        if (!opt) return null;
        if (typeof opt === 'string') return { answer: opt };
        if (typeof opt.answer === 'string') return opt;
        return null;
    };

    // Gérer les deux formats de réponses correctes (and array in correct_answer)
    const correctSingles = Array.isArray(q.correct_answer)
        ? q.correct_answer.map(normalize).filter(Boolean)
        : (q.correct_answer ? [normalize(q.correct_answer)].filter(Boolean) : []);
    const correctMultis = Array.isArray(q.correct_answers) ? q.correct_answers.map(normalize).filter(Boolean) : [];
    const correctOptions = [...correctSingles, ...correctMultis];
    const correctAnswers = correctOptions.map(o => o.answer);
    const distractorOptions = Array.isArray(q.distractors) ? q.distractors.map(normalize).filter(Boolean) : [];

    const distractors = Array.isArray(q.distractors) ? q.distractors.map(normalize).filter(Boolean) : [];

    return (
        <div className="space-y-6">
            <div className="text-blue-300 font-bold text-xl">Solution</div>

            {/* Question detail */}
            {q?.question && (
                <div className="bg-gray-800 rounded-lg p-4 border-l-4 border-yellow-500 shadow-lg">
                    <h3 className="text-lg font-semibold text-yellow-300 mb-2">Question</h3>
                    <div className="text-lg leading-relaxed">
                        <SectionContent content={q.question} />
                    </div>
            </div>
            )}

            {/* LLM Evaluation Section - Show for free text answers */}
            {currentQCM?.llmEvaluation && (
                <div className="bg-gray-800 rounded-lg p-4 border-l-4 border-purple-500 shadow-lg">
                    <div className="flex items-center mb-4">
                        <div className="mr-3">
                            <svg className="w-5 h-5 text-purple-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <h2 className="text-xl font-bold text-purple-300">Evaluation of the given answer</h2>
                    </div>
                    <div className="text-lg leading-relaxed">
                        <div className="mb-4">
                                                                        <p className="text-gray-300 mb-2">
                                                <span className="font-semibold text-green-400">Score:</span> {currentQCM.llmEvaluation.score}/100 
                                                <span className="text-gray-500 ml-2">(Threshold: {currentQCM.llmEvaluation.threshold})</span>
                                            </p>
                            <p className="text-gray-300">
                                <span className="font-semibold text-blue-400">Explanation:</span> {currentQCM.llmEvaluation.explanation}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Score Section */}
            {lastFunnelResult && (
                <div className="bg-gray-800 rounded-lg p-6 border-l-4 border-gray-500 shadow-lg">
                    <div className="flex items-center mb-6">
                        <div className="mr-3">
                            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                            </svg>
                        </div>
                        <h3 className="text-xl font-bold text-gray-300">Score</h3>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-8">
                        {/* Donut Chart */}
                        <div className="flex flex-col items-center justify-center">
                            <div className="w-32 h-32 mb-3">
                                <Doughnut
                                    data={{
                                        labels: ['Correct', 'Incorrect'],
                                        datasets: [{
                                            data: [lastFunnelResult.score, Math.max(0, lastFunnelResult.total - lastFunnelResult.score)],
                                            backgroundColor: ['#22c55e', '#334155'],
                                            borderWidth: 0
                                        }]
                                    }}
                                    options={{ plugins: { legend: { display: false } }, cutout: '70%' }}
                                />
                            </div>
                            <div className="text-center">
                                <div className="text-blue-300 font-semibold text-xl">{Math.round((lastFunnelResult.score / lastFunnelResult.total) * 100)}%</div>
                                <div className="text-gray-400 text-base">% of correct answers</div>
                            </div>
                        </div>

                        {/* Penalty Score Gauge */}
                        <div className="flex flex-col justify-center">
                            <div className="text-gray-300 font-semibold text-lg mb-4">With Penalty and confidence ponderation</div>
                            {(() => {
                                // Calculate penalty score
                                let penaltyScore = 0;
                                const confidenceWeights = { low: 0.2, medium: 0.5, high: 1 };
                                
                                if (lastFunnelResult.choices) {
                                    Object.entries(lastFunnelResult.choices).forEach(([answer, userChoice]) => {
                                        const isCorrect = correctAnswers.includes(answer);
                                        const weight = confidenceWeights[userChoice.confidence] || 0.5;
                                        
                                        if (userChoice.choice === 'OK') {
                                            penaltyScore += isCorrect ? (1 * weight) : (-1 * weight);
                                        } else if (userChoice.choice === 'KO') {
                                            penaltyScore += isCorrect ? (-1 * weight) : (1 * weight);
                                        }
                                    });
                                }
                                
                                const maxPossible = lastFunnelResult.total;
                                const minPossible = -maxPossible;
                                const normalizedPosition = Math.max(0, Math.min(100, ((penaltyScore - minPossible) / (maxPossible - minPossible)) * 100));
                                
                                return (
                                    <div className="space-y-3">
                                        <div className="flex justify-between text-sm text-gray-400">
                                            <span>{minPossible.toFixed(1)}</span>
                                            <span>0</span>
                                            <span>{maxPossible.toFixed(1)}</span>
                                        </div>
                                        <div className="w-full h-4 bg-gray-700 rounded-full relative overflow-hidden">
                                            <div className="absolute inset-0 bg-gradient-to-r from-red-500 via-yellow-500 to-green-500"></div>
                                            <div 
                                                className="absolute top-0 w-1 h-full bg-white border border-gray-800" 
                                                style={{ left: `${normalizedPosition}%`, transform: 'translateX(-50%)' }}
                                            ></div>
                                        </div>
                                        <div className="text-center text-gray-300 font-semibold text-lg">
                                            Score: {penaltyScore.toFixed(2)}
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>

                        {/* Weakness Focus */}
                        <div className="flex flex-col">
                            <div className="text-gray-300 font-semibold text-lg mb-4">Weakness focus</div>
                            {(() => {
                                const incorrectAnswers = [];
                                const confidenceWeights = { high: 3, medium: 2, low: 1 };
                                
                                if (lastFunnelResult.choices) {
                                    Object.entries(lastFunnelResult.choices).forEach(([answer, userChoice]) => {
                                        const isCorrect = correctAnswers.includes(answer);
                                        const isUserCorrect = (userChoice.choice === 'OK' && isCorrect) || (userChoice.choice === 'KO' && !isCorrect);
                                        
                                        if (!isUserCorrect) {
                                            // Find the option to get keywords
                                            const option = [...correctOptions, ...distractorOptions].find(opt => opt.answer === answer);
                                            const keywords = option?.keywords || [];
                                            const weight = confidenceWeights[userChoice.confidence] || 1;
                                            
                                            incorrectAnswers.push({
                                                answer,
                                                keywords,
                                                weight,
                                                confidence: userChoice.confidence
                                            });
                                        }
                                    });
                                }
                                
                                // Sort by confidence weight DESC
                                incorrectAnswers.sort((a, b) => b.weight - a.weight);
                                
                                return (
                                    <div className="space-y-2 max-h-40 overflow-y-auto">
                                        {incorrectAnswers.length > 0 ? (
                                            incorrectAnswers.map((item, idx) => (
                                                <div key={idx} className="text-sm text-gray-300 border-l-2 border-red-500/30 pl-3 py-1">
                                                    <div className="font-medium text-base">{item.keywords.join(', ')}</div>
                                                    <div className="text-gray-400 text-sm">({item.confidence} confidence)</div>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="text-sm text-gray-400">No weaknesses identified</div>
                                        )}
                                    </div>
                                );
                            })()}
                        </div>
                    </div>
                </div>
            )}

            {/* Correct Option(s) */}
            <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-green-400 mb-3 flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {correctAnswers.length > 1 ? 'Correct Option(s)' : 'Correct Option(s)'}
                </h3>
                {correctOptions.length > 0 && (
                    <div className="space-y-4">
                        {correctOptions.map((opt, idx) => {
                            const answer = opt.answer;
                            const user = lastFunnelResult?.choices?.[answer];
                            const userOk = user ? (user.choice === 'OK') : null;
                            return (
                                <div key={idx} className="bg-gray-800 rounded p-3">
                                    <div className="flex items-start gap-3 mb-2">
                                        <div className="mt-1">
                                            {userOk === null ? (
                                                <span className="w-4 h-4 inline-block rounded-full bg-gray-500"></span>
                                            ) : userOk ? (
                                                <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                            ) : (
                                                <svg className="w-4 h-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                            )}
                                        </div>
                                        <div className="flex-1">
                                            <div className="text-gray-100 font-medium">{answer}</div>
                                            <div className="text-gray-400 text-sm mt-1">Your confidence level: <span className="text-gray-200 font-medium">{user?.confidence || '-'}</span></div>
                                        </div>
                                    </div>
                                    {opt?.explanation && (
                                        <div className="text-gray-200 leading-relaxed text-lg mt-2">
                                            <span className="font-semibold text-green-300 mr-2">Why it's correct:</span>
                                            {formatExplanation(opt.explanation)}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                {(!correctOptions.some(o => o?.explanation) && currentQCM.summary?.problem_solution) && (
                    <div className="text-gray-200 leading-relaxed text-lg">
                        {formatExplanation(currentQCM.summary.problem_solution)}
                    </div>
                )}
            </div>

            {/* Tips Section - Moved from Summary (optional) */}
            {(currentQCM?.tips || q?.tips || qcmSource?.tips) && (
                <div className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-purple-400 mb-3 flex items-center">
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                        </svg>
                        Tips
                    </h3>
                    <div className="bg-gray-800 rounded p-3">
                        <p className="text-gray-300 leading-relaxed">
                            {currentQCM?.tips || q?.tips || qcmSource?.tips}
                        </p>
                    </div>
                </div>
            )}

            {/* LLM Evaluation Section - Only show for correct free text answers */}
            {currentQCM?.llmEvaluation && currentQCM.llmEvaluation.is_correct && (
                <div className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-purple-400 mb-3 flex items-center">
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Evaluation of the given answer
                    </h3>
                    
                    <div className="bg-gray-800 rounded p-3 mb-3">
                        <div className="space-y-2">
                            <p className="text-gray-200">
                                <span className="font-semibold text-green-400">Score:</span> {currentQCM.llmEvaluation.score}/10 
                                <span className="text-gray-500 ml-2">(Threshold: {currentQCM.llmEvaluation.threshold})</span>
                            </p>
                            <p className="text-gray-200">
                                <span className="font-semibold text-blue-400">Explanation:</span> {currentQCM.llmEvaluation.explanation}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Incorrect Options Explanation with user interaction */}
            {distractorOptions.length > 0 && (
                <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-red-400 mb-3 flex items-center">
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Why Other Options Are Incorrect
                    </h3>
                    
                    <div className="space-y-4">
                        {distractorOptions.map((distractor, index) => {
                            const failureMap = currentQCM.summary?.distractor_failures || {};
                            const fallback = failureMap[distractor.answer];
                            const explanationText = distractor.explanation || fallback || '';
                            const user = lastFunnelResult?.choices?.[distractor.answer];
                            const userOk = user ? (user.choice === 'KO') : null;
                            return (
                                <div key={index} className="bg-gray-800 rounded p-3">
                                    <div className="flex items-start gap-3 mb-2">
                                        <div className="mt-1">
                                            {userOk === null ? (
                                                <span className="w-4 h-4 inline-block rounded-full bg-gray-500"></span>
                                            ) : userOk ? (
                                                <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                            ) : (
                                                <svg className="w-4 h-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                            )}
                                        </div>
                                        <div className="flex-1">
                                            <div className="text-gray-100 font-medium">{distractor.answer}</div>
                                            <div className="text-gray-400 text-sm mt-1">Your confidence level: <span className="text-gray-200 font-medium">{user?.confidence || '-'}</span></div>
                                        </div>
                                    </div>
                                    {explanationText && (
                                        <div className="text-gray-300 text-lg mt-2">
                                            <span className="font-semibold text-red-300 mr-2">Why it's not correct:</span>
                                            {formatExplanation(explanationText)}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}



            {/* Common Error */}
            {(currentQCM?.common_error || qcmSource?.common_error) && (
                <div className="bg-orange-900/20 border border-orange-500/30 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-orange-400 mb-3 flex items-center">
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.996-.833-2.768 0L3.045 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                        Common Error
                    </h3>
                    <p className="text-gray-300">
                        {currentQCM?.common_error || qcmSource?.common_error}
                    </p>
                </div>
            )}

            {/* Room for Improvement (optional) */}
            {currentQCM?.improvement_list && Array.isArray(currentQCM.improvement_list) && currentQCM.improvement_list.length > 0 && (
            <RoomForImprovement />
            )}
        </div>
    );
};

// Helper function to format explanation text
const formatExplanation = (text) => {
    if (!text) return '';
    
    // Split by common patterns and format
    const parts = text.split(/(\b(?:Why it's correct|Why it's incorrect|Explanation of Concept)\b:?)/gi);
    
    return parts.map((part, index) => {
        if (part.match(/Why it's correct/i)) {
            return <span key={index} className="font-semibold text-green-300">{part}</span>;
        } else if (part.match(/Why it's incorrect/i)) {
            return <span key={index} className="font-semibold text-red-300">{part}</span>;
        } else if (part.match(/Explanation of Concept/i)) {
            return <span key={index} className="font-semibold text-blue-300">{part}</span>;
        } else if (part.trim()) {
            return <span key={index}>{part}</span>;
        }
        return null;
    }).filter(Boolean);
};

export default QCMSolution;